import { getDayOfYear } from 'date-fns';
import { createSlice, current } from '@reduxjs/toolkit';
import localForage from 'localforage';
import {
  getAllExpensesTypes,
  getAllIncomeTypes,
  getAllPayType,
  getAllStatementsContributionType,
  getAllStatementsFeeType,
  submitIncomeStatement,
  getIncomeStatements,
  getUnsubmittedIncomeItems,
  getUnsubmittedExpenseItems,
  addIncomeItem,
  addExpenseItem,
  deleteIncomeItem,
  deleteExpenseItem,
} from './statements.actions';
import { StatementsState } from './statements.types';
import { setFulfilledThunkActionStatus, setThunkActionStatus } from '../utils';
import { ExpenseItemDto, ExpenseTypeIds, IncomeTypeIds } from '@ecdlink/core';
import { normalizeDateToMidnight } from '@/utils/common/date.utils';

/**
 * Removes a deleted income or expense item from the statements held in state, and recomputes the
 * affected statement's totals.
 *
 * Deleting only cleaned up unSubmittedIncomeItems / unsyncedIncomeItems, never the copies nested
 * inside incomeStatements. Screens that read a submitted statement - MonthStatements passes
 * statement.incomeItems straight down - therefore kept showing the entry, and it came back as
 * active as soon as the screen was remounted. Online a later refetch hid that; offline there is
 * no refetch, so the entry stayed for good.
 */
const removeItemFromStatements = (
  state: StatementsState,
  itemId: string,
  itemType: 'income' | 'expense'
) => {
  state.incomeStatements = state.incomeStatements?.map((statement) => {
    const isIncome = itemType === 'income';

    const incomeItems = isIncome
      ? statement.incomeItems?.filter((item) => item.id !== itemId)
      : statement.incomeItems;
    const expenseItems = !isIncome
      ? statement.expenseItems?.filter((item) => item.id !== itemId)
      : statement.expenseItems;

    const removed = isIncome
      ? (incomeItems?.length ?? 0) !== (statement.incomeItems?.length ?? 0)
      : (expenseItems?.length ?? 0) !== (statement.expenseItems?.length ?? 0);

    if (!removed) return statement;

    const incomeTotal = sumItemAmounts(incomeItems);
    const expenseTotal = sumItemAmounts(expenseItems);

    return {
      ...statement,
      incomeItems,
      expenseItems,
      incomeTotal,
      expenseTotal,
      balance: incomeTotal - expenseTotal,
    };
  });
};

const sumItemAmounts = (items?: { amount: number }[]) =>
  (items ?? []).reduce((total, item) => total + (item.amount ?? 0), 0);

const initialState: StatementsState = {
  expensesTypes: undefined,
  incomeTypes: undefined,
  feeTypes: undefined,
  contributionTypes: undefined,
  payTypes: undefined,
  incomeStatements: [],
  unSubmittedIncomeItems: [],
  unSubmittedExpenseItems: [],
  unsyncedIncomeItems: [],
  unsyncedExpenseItems: [],
  unsyncedDeletedIncomeItemIds: [],
  unsyncedDeletedExpenseItemIds: [],
};

const statementsSlice = createSlice({
  name: 'statements',
  initialState,
  reducers: {
    resetStatementsState: (state) => {
      state.expensesTypes = initialState.expensesTypes;
      state.incomeTypes = initialState.incomeTypes;
      state.feeTypes = initialState.feeTypes;
      state.contributionTypes = initialState.contributionTypes;
      state.incomeStatements = initialState.incomeStatements;
      state.unSubmittedIncomeItems = initialState.unSubmittedIncomeItems;
      state.unSubmittedExpenseItems = initialState.unSubmittedExpenseItems;
      state.unsyncedIncomeItems = initialState.unsyncedIncomeItems;
      state.unsyncedExpenseItems = initialState.unsyncedExpenseItems;
      state.unsyncedDeletedIncomeItemIds =
        initialState.unsyncedDeletedIncomeItemIds;
      state.unsyncedDeletedExpenseItemIds =
        initialState.unsyncedDeletedExpenseItemIds;
      state.payTypes = initialState.payTypes;
    },
  },
  extraReducers: (builder) => {
    setThunkActionStatus(builder, submitIncomeStatement);
    builder.addCase(getAllExpensesTypes.fulfilled, (state, action) => {
      state.expensesTypes = action.payload;
    });
    builder.addCase(getAllIncomeTypes.fulfilled, (state, action) => {
      state.incomeTypes = action.payload;
    });
    builder.addCase(getAllStatementsFeeType.fulfilled, (state, action) => {
      state.feeTypes = action.payload;
    });
    builder.addCase(
      getAllStatementsContributionType.fulfilled,
      (state, action) => {
        state.contributionTypes = action.payload;
      }
    );
    builder.addCase(getAllPayType.fulfilled, (state, action) => {
      state.payTypes = action.payload;
    });
    builder.addCase(submitIncomeStatement.fulfilled, (state, action) => {
      setFulfilledThunkActionStatus(state, action);

      // Check if we have already synced the statement, if not add it to the state
      if (
        state.incomeStatements.findIndex(
          (statement) =>
            statement.year === action.payload.year &&
            statement.month === action.payload.month
        ) < 0
      ) {
        state.incomeStatements = [...state.incomeStatements, action.payload];
      }

      state.unsyncedIncomeItems = [];
      state.unsyncedExpenseItems = [];
      state.unsyncedDeletedIncomeItemIds = [];
      state.unsyncedDeletedExpenseItemIds = [];
      state.unSubmittedIncomeItems = [];
      state.unSubmittedExpenseItems = [];
    });
    builder.addCase(getIncomeStatements.fulfilled, (state, action) => {
      state.incomeStatements = action.payload;
    });
    builder.addCase(getUnsubmittedIncomeItems.fulfilled, (state, action) => {
      state.unSubmittedIncomeItems = action.payload;
    });
    builder.addCase(getUnsubmittedExpenseItems.fulfilled, (state, action) => {
      state.unSubmittedExpenseItems = action.payload;
    });
    builder.addCase(addIncomeItem.fulfilled, (state, action) => {
      const index = state.unSubmittedIncomeItems.findIndex(
        (x) => x.id === action.payload.id
      );

      const incomeTypeOther = state.incomeStatements?.find(
        (item) => item?.id === IncomeTypeIds.OTHER_INCOME_ID
      );

      const exists = current(state.unSubmittedIncomeItems)?.some((item) => {
        return (
          item.amount === action.payload?.amount &&
          normalizeDateToMidnight(item.dateReceived)?.getTime() ===
            normalizeDateToMidnight(action.payload?.dateReceived)?.getTime() &&
          item.description === action.payload?.description
        );
      });

      if (index < 0) {
        if (incomeTypeOther?.id === action.payload.incomeTypeId) {
          if (!exists) {
            state.unSubmittedIncomeItems = [
              ...state.unSubmittedIncomeItems,
              action.payload,
            ];
          }
          return;
        } else {
          state.unSubmittedIncomeItems = [
            ...state.unSubmittedIncomeItems,
            action.payload,
          ];
        }
      } else {
        state.unSubmittedIncomeItems = [
          ...state.unSubmittedIncomeItems.slice(0, index),
          action.payload,
          ...state.unSubmittedIncomeItems.slice(index + 1),
        ];
      }

      // Remove from unsynced
      const unsyncedIndex = state.unsyncedIncomeItems.findIndex(
        (x) => x.Id === action.payload.id
      );

      if (unsyncedIndex >= 0) {
        state.unsyncedIncomeItems = [
          ...state.unsyncedIncomeItems.slice(0, unsyncedIndex),
          ...state.unsyncedIncomeItems.slice(unsyncedIndex + 1),
        ];
      }
    });
    builder.addCase(addIncomeItem.rejected, (state, action) => {
      if (action.meta.arg.firstAttempt) {
        const input = action.meta.arg.input;

        const index = state.unSubmittedIncomeItems.findIndex((x) => {
          return x.id === input.Id;
        });

        const incomeTypeOther = state.incomeTypes?.find(
          (item) => item?.id === IncomeTypeIds.OTHER_INCOME_ID
        );

        const exists = state.unSubmittedIncomeItems.some(
          (item) =>
            item.amount === input?.Amount &&
            normalizeDateToMidnight(item.dateReceived)?.getTime() ===
              normalizeDateToMidnight(input?.DateReceived)?.getTime() &&
            item.description === input?.Description
        );

        if (index < 0) {
          state.unsyncedIncomeItems = [...state.unsyncedIncomeItems, input];

          if (incomeTypeOther?.id === input.IncomeTypeId && exists) {
            return;
          }
          // Map and add to our unsubmitted items
          state.unSubmittedIncomeItems = [
            ...state.unSubmittedIncomeItems,
            {
              amount: input.Amount,
              dateReceived: input.DateReceived,
              id: input.Id,
              incomeTypeId: input.IncomeTypeId!,
              childUserId: input.ChildUserId as string,
              amountExpected: input.AmountExpected,
              description: input.Description || '',
              photoProof: input.PhotoProof || '',
              payTypeId: input.PayTypeId!,
              feeTypeId: input.FeeTypeId!,
              contributionTypeId: input.ContributionTypeId!,
              notes: input.Notes || '',
              childCoverAmount: input.ChildCoverAmount || 0,
            },
          ];
        } else {
          const input = action.meta.arg.input;
          const unsyncedIndex = state.unsyncedIncomeItems.findIndex(
            (x) => x.Id === input?.Id
          );

          state.unSubmittedIncomeItems = [
            ...state.unSubmittedIncomeItems.slice(0, index),
            {
              amount: input.Amount,
              dateReceived: input.DateReceived,
              id: input.Id,
              incomeTypeId: input.IncomeTypeId!,
              description: input.Description || '',
              childUserId: input.ChildUserId as string,
              photoProof: input.PhotoProof || '',
              amountExpected: input.AmountExpected,
              payTypeId: input.PayTypeId!,
              feeTypeId: input.FeeTypeId!,
              contributionTypeId: input.ContributionTypeId!,
              notes: input.Notes || '',
              childCoverAmount: input.ChildCoverAmount || 0,
            },
            ...state.unSubmittedIncomeItems.slice(index + 1),
          ];

          if (unsyncedIndex >= 0) {
            state.unsyncedIncomeItems = [...state.unsyncedIncomeItems, input];
            state.unsyncedIncomeItems = [
              ...state.unsyncedIncomeItems.slice(0, unsyncedIndex),
              ...state.unsyncedIncomeItems.slice(unsyncedIndex + 1),
            ];
          } else {
            state.unsyncedIncomeItems = [...state.unsyncedIncomeItems, input];
          }
        }
      }
    });
    builder.addCase(addExpenseItem.fulfilled, (state, action) => {
      const index = state.unSubmittedExpenseItems.findIndex(
        (x) => x.id === action.payload.id
      );

      const expensesTypeOther = state.expensesTypes?.find(
        (item) => item?.id === ExpenseTypeIds.OTHER_EXPENSE_ID
      );

      const exists = current(state.unSubmittedExpenseItems)?.some((item) => {
        return (
          item.amount === action.payload?.amount &&
          normalizeDateToMidnight(item.datePaid)?.getTime() ===
            normalizeDateToMidnight(action.payload?.datePaid)?.getTime() &&
          item.description === action.payload?.description
        );
      });

      if (index < 0) {
        if (expensesTypeOther?.id === action.payload.expenseTypeId) {
          if (!exists) {
            state.unSubmittedExpenseItems = [
              ...state.unSubmittedExpenseItems,
              action.payload,
            ];
          }
          return;
        } else {
          state.unSubmittedExpenseItems = [
            ...state.unSubmittedExpenseItems,
            action.payload,
          ];
        }
      } else {
        state.unSubmittedExpenseItems = [
          ...state.unSubmittedExpenseItems.slice(0, index),
          action.payload,
          ...state.unSubmittedExpenseItems.slice(index + 1),
        ];
      }

      // Remove from unsynced
      const unsyncedIndex = state.unsyncedExpenseItems.findIndex(
        (x) => x.Id === action.payload.id
      );

      if (unsyncedIndex >= 0) {
        state.unsyncedExpenseItems = [
          ...state.unsyncedExpenseItems.slice(0, unsyncedIndex),
          ...state.unsyncedExpenseItems.slice(unsyncedIndex + 1),
        ];
      }
    });
    builder.addCase(addExpenseItem.rejected, (state, action) => {
      if (action.meta.arg.firstAttempt) {
        const input = action.meta.arg.input;

        const index = state.unSubmittedExpenseItems.findIndex((x) => {
          return x.id === input.Id;
        });

        const expensesTypeOther = state.expensesTypes?.find(
          (item) => item?.id === ExpenseTypeIds.OTHER_EXPENSE_ID
        );

        const exists = state.unSubmittedExpenseItems.some(
          (item) =>
            item.amount === input?.Amount &&
            normalizeDateToMidnight(item.datePaid)?.getTime() ===
              normalizeDateToMidnight(input?.DatePaid)?.getTime() &&
            item.description === input?.Description
        );

        if (index < 0) {
          state.unsyncedExpenseItems = [...state.unsyncedExpenseItems, input];

          if (expensesTypeOther?.id === input.ExpenseTypeId && exists) {
            return;
          }

          state.unSubmittedExpenseItems = [
            ...state.unSubmittedExpenseItems,
            {
              amount: input.Amount,
              datePaid: input.DatePaid,
              id: input.Id,
              expenseTypeId: input.ExpenseTypeId!,
              description: input.Description || '',
            },
          ];
        } else {
          const input = action.meta.arg.input;
          const unsyncedIndex = state.unsyncedExpenseItems.findIndex(
            (x) => x.Id === input?.Id
          );
          state.unSubmittedExpenseItems = [
            ...state.unSubmittedExpenseItems.slice(0, index),
            {
              amount: input.Amount,
              datePaid: input.DatePaid,
              id: input.Id,
              expenseTypeId: input.ExpenseTypeId!,
              description: input.Description || '',
            },
            ...state.unSubmittedExpenseItems.slice(index + 1),
          ] as ExpenseItemDto[];

          if (unsyncedIndex >= 0) {
            state.unsyncedExpenseItems = [...state.unsyncedExpenseItems, input];
            state.unsyncedExpenseItems = [
              ...state.unsyncedExpenseItems.slice(0, unsyncedIndex),
              ...state.unsyncedExpenseItems.slice(unsyncedIndex + 1),
            ];
          } else {
            state.unsyncedExpenseItems = [...state.unsyncedExpenseItems, input];
          }
        }
      }
    });

    builder.addCase(deleteIncomeItem.fulfilled, (state, action) => {
      const deletedItemId = action.meta.arg.id;

      state.unSubmittedIncomeItems = state.unSubmittedIncomeItems.filter(
        (item) => item.id !== deletedItemId
      );

      state.unsyncedIncomeItems = state.unsyncedIncomeItems.filter(
        (item) => item.Id !== deletedItemId
      );

      state.unsyncedDeletedIncomeItemIds =
        state.unsyncedDeletedIncomeItemIds.filter((id) => id !== deletedItemId);

      removeItemFromStatements(state, deletedItemId, 'income');
    });

    builder.addCase(deleteIncomeItem.rejected, (state, action) => {
      const deletedItemId = action.meta.arg.id;
      const wasUnsyncedItem = state.unsyncedIncomeItems.some(
        (item) => item.Id === deletedItemId
      );

      const hasUnsubmittedItem = state.unSubmittedIncomeItems.some(
        (item) => item.id === deletedItemId
      );
      const hasQueuedDelete =
        state.unsyncedDeletedIncomeItemIds.includes(deletedItemId);

      if (!hasUnsubmittedItem && !wasUnsyncedItem && hasQueuedDelete) {
        return;
      }

      state.unSubmittedIncomeItems = state.unSubmittedIncomeItems.filter(
        (item) => item.id !== deletedItemId
      );

      state.unsyncedIncomeItems = state.unsyncedIncomeItems.filter(
        (item) => item.Id !== deletedItemId
      );

      if (
        !wasUnsyncedItem &&
        !state.unsyncedDeletedIncomeItemIds.includes(deletedItemId)
      ) {
        state.unsyncedDeletedIncomeItemIds = [
          ...state.unsyncedDeletedIncomeItemIds,
          deletedItemId,
        ];
      }

      removeItemFromStatements(state, deletedItemId, 'income');
    });

    builder.addCase(deleteExpenseItem.fulfilled, (state, action) => {
      const deletedItemId = action.meta.arg.id;

      state.unSubmittedExpenseItems = state.unSubmittedExpenseItems.filter(
        (item) => item.id !== deletedItemId
      );

      state.unsyncedExpenseItems = state.unsyncedExpenseItems.filter(
        (item) => item.Id !== deletedItemId
      );

      state.unsyncedDeletedExpenseItemIds =
        state.unsyncedDeletedExpenseItemIds.filter(
          (id) => id !== deletedItemId
        );

      removeItemFromStatements(state, deletedItemId, 'expense');
    });

    builder.addCase(deleteExpenseItem.rejected, (state, action) => {
      const deletedItemId = action.meta.arg.id;
      const wasUnsyncedItem = state.unsyncedExpenseItems.some(
        (item) => item.Id === deletedItemId
      );

      const hasUnsubmittedItem = state.unSubmittedExpenseItems.some(
        (item) => item.id === deletedItemId
      );
      const hasQueuedDelete =
        state.unsyncedDeletedExpenseItemIds.includes(deletedItemId);

      if (!hasUnsubmittedItem && !wasUnsyncedItem && hasQueuedDelete) {
        return;
      }

      state.unSubmittedExpenseItems = state.unSubmittedExpenseItems.filter(
        (item) => item.id !== deletedItemId
      );

      state.unsyncedExpenseItems = state.unsyncedExpenseItems.filter(
        (item) => item.Id !== deletedItemId
      );

      if (
        !wasUnsyncedItem &&
        !state.unsyncedDeletedExpenseItemIds.includes(deletedItemId)
      ) {
        state.unsyncedDeletedExpenseItemIds = [
          ...state.unsyncedDeletedExpenseItemIds,
          deletedItemId,
        ];
      }

      removeItemFromStatements(state, deletedItemId, 'expense');
    });
  },
});

const { reducer: statementsReducer, actions: statementsActions } =
  statementsSlice;

const statementsPersistConfig = {
  key: 'statements',
  storage: localForage,
  blacklist: [],
};

export { statementsPersistConfig, statementsReducer, statementsActions };
